import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { devToolsAvailable } from './dev-tools';
import { CREDIT_MINER_SPEC, setToggleOverride } from './dev-feature-toggles';
import { patchAttachmentsById } from './message-reactions';
import type { Message } from './use-chat';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';

/**
 * Dev-only: plays a generated picture's whole arrival in the OPEN chat, the way production delivers it —
 * the reply appears holding a placeholder, the placeholder is filled with an address after the render
 * time, and that address answers like the real one (an optional redirect hop, then the bytes after the
 * chosen delay). Nothing is sent to the server: the row is local to this page, and while a run is live
 * the Credit Miner's progress/claim calls are answered in memory so a test render never touches the real
 * account's save or balance.
 */

/** The shapes a render comes back in, at the sizes the image service actually returns, plus a tiny one. */
export const PICTURE_SHAPES = [
  { id: 'square', label: 'Square 1:1', w: 1024, h: 1024 },
  { id: 'landscape', label: 'Landscape 3:2', w: 1536, h: 1024 },
  { id: 'wide', label: 'Wide 16:9', w: 1792, h: 1008 },
  { id: 'panorama', label: 'Panorama 3:1', w: 1920, h: 640 },
  { id: 'portrait', label: 'Portrait 2:3', w: 1024, h: 1536 },
  { id: 'tall', label: 'Tall 9:16', w: 1008, h: 1792 },
  { id: 'strip', label: 'Strip 1:3', w: 640, h: 1920 },
  { id: 'tiny', label: 'Tiny 96×64', w: 96, h: 64 },
] as const;

export type PictureShape = (typeof PICTURE_SHAPES)[number];

export interface PictureArrivalRun {
  shape: PictureShape;
  /** How long the picture's bytes take to start arriving once its address is requested. */
  imageDelayMs: number;
  /** Put a redirect in front of the bytes, as the image service's share link does (~420ms, uncached). */
  redirectHop: boolean;
  /** How long the render takes before the placeholder is filled with the picture's address. */
  renderMs: number;
}

const EVENT = 'symphony:dev-picture-arrival';
type Arrival = { kind: 'append'; message: Message } | { kind: 'fill'; id: string; attachments: MessageAttachment[] };

export function startPictureArrival(run: PictureArrivalRun): void {
  if (!devToolsAvailable) return;
  answerCreditMinerLocally();
  const id = `dev-picture-${Date.now()}`;
  const placeholder: MessageAttachment = { fileName: run.shape.label, mimeType: 'image/png', url: '' };
  emit({
    kind: 'append',
    message: { id, role: 'ASSISTANT', content: `Here is your ${run.shape.label} picture.`, createdDate: new Date().toISOString(), attachments: [placeholder] },
  });
  setTimeout(() => emit({ kind: 'fill', id, attachments: [{ ...placeholder, fileName: `${run.shape.label}.png`, url: pictureUrl(run) }] }), run.renderMs);
}

/** Lets the open chat receive the lab's rows. A no-op outside a dev build. */
export function useDevPictureArrival(setMessages: Dispatch<SetStateAction<Message[]>>): void {
  useEffect(() => {
    if (!devToolsAvailable) return;
    const onArrival = (e: Event) => {
      const arrival = (e as CustomEvent<Arrival>).detail;
      if (arrival.kind === 'append') setMessages((prev) => [...prev, arrival.message]);
      else setMessages((prev) => patchAttachmentsById(prev, arrival.id, arrival.attachments));
    };
    window.addEventListener(EVENT, onArrival);
    return () => window.removeEventListener(EVENT, onArrival);
  }, [setMessages]);
}

/** Runs the `?picture-arrival=every` autoplay; mounted where the app always is, since the chat mounts only once opened. */
export function useDevPictureArrivalAutoplay(): void {
  const { activeConversationId, setChatMode, openMaestroChat } = useActiveConversation();
  const open = useRef(() => (activeConversationId ? setChatMode('expanded') : openMaestroChat()));
  useEffect(() => {
    if (!devToolsAvailable) return;
    return autoplayFromUrl(() => open.current());
  }, []);
}

/** Time for the chat to mount after it is opened, before the first row is sent to it. */
const CHAT_OPEN_MS = 4000;

const REDIRECT_MS = 430;
const SETTLE_MS = 2500;

/** Plays runs one after another, each starting once the previous one has settled. Returns a cancel. */
export function playPictureArrivals(runs: PictureArrivalRun[], startAt = 0): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];
  let at = startAt;
  for (const run of runs) {
    timers.push(setTimeout(() => startPictureArrival(run), at));
    at += run.renderMs + (run.redirectHop ? REDIRECT_MS : 0) + run.imageDelayMs + SETTLE_MS;
  }
  return () => timers.forEach(clearTimeout);
}

/**
 * `?picture-arrival=every` opens the chat and plays every shape in it without touching the panel;
 * `&delays=0,1000,3000` repeats the set per delay, `&game=off` shows the shimmer, `&render=` sets the render
 * time in ms. A dev build only.
 */
function autoplayFromUrl(openChat: () => void): (() => void) | undefined {
  const query = new URLSearchParams(window.location.search);
  if (query.get('picture-arrival') !== 'every') return undefined;
  const game = query.get('game') !== 'off';
  setToggleOverride(CREDIT_MINER_SPEC, game ? 'true' : 'false', false);
  // After the app's own first-load pass, which settles the chat's starting mode and would close it again.
  const opening = setTimeout(openChat, CHAT_OPEN_MS / 2);
  const delays = (query.get('delays') ?? '0,1000,3000').split(',').map(Number).filter((n) => n >= 0);
  const renderMs = Number(query.get('render') ?? 4000);
  const runs = delays.flatMap((imageDelayMs) => PICTURE_SHAPES.map((shape) => ({ shape, imageDelayMs, renderMs, redirectHop: true })));
  const stop = playPictureArrivals(runs, CHAT_OPEN_MS);
  return () => {
    clearTimeout(opening);
    stop();
  };
}

function emit(arrival: Arrival): void {
  window.dispatchEvent(new CustomEvent<Arrival>(EVENT, { detail: arrival }));
}

function pictureUrl(run: PictureArrivalRun): string {
  const query = new URLSearchParams({
    w: String(run.shape.w),
    h: String(run.shape.h),
    delay: String(run.imageDelayMs),
    hop: run.redirectHop ? '1' : '0',
    // A fresh address per run, so nothing is served from an earlier run's cache.
    run: String(Date.now()),
  });
  return `${window.location.origin}/api/dev/picture?${query}`;
}

let answering = false;
function answerCreditMinerLocally(): void {
  if (answering) return;
  answering = true;
  const network = window.fetch.bind(window);
  let progress: unknown = null;
  let bankedMicro = 0;
  const json = (body: unknown) => new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json' } });
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!url.includes('/api/credit-miner/')) return network(input, init);
    const body = typeof init?.body === 'string' ? JSON.parse(init.body) : {};
    if (url.includes('/api/credit-miner/claim')) {
      const microcents = Number(body.microcents) || 0;
      bankedMicro += microcents;
      const wholeCredits = Math.floor(bankedMicro / 1_000_000);
      bankedMicro -= wholeCredits * 1_000_000;
      return json({ requestedMicro: microcents, claimedMicro: microcents, bankedMicro, wholeCredits, pendingCredits: 0, grantedCredits: wholeCredits, renders: 1 });
    }
    if (init?.method && init.method !== 'GET') {
      progress = body.progress ?? progress;
      return json({});
    }
    return json({ enabled: true, progress, claimedMicro: 0, bankedMicro, grantedCredits: 0, pendingCredits: 0, renders: 0, resetAt: null });
  };
}
